import { type ReactNode, useState } from 'react'
import { Link } from 'react-router-dom'
import { Sun, Moon, Globe, ChevronDown, History, Shield, HelpCircle, MessageCircle, Zap, ArrowRight, Gift } from 'lucide-react'
import { useTheme } from '@/contexts/ThemeContext'
import { useThemeColors } from '@/hooks/useThemeColors'
import { useUI } from '@/hooks/useUI'
import { useViewportSize } from '@/hooks/useViewportSize'
import { LogoMark } from '@/components/LogoMark'
import { ReferralComingSoonModal } from '@/components/ReferralComingSoonModal'
import { FONT_HEADING, FONT_BODY, BRAND_CYAN, BRAND_GREEN } from '@/config/theme'

interface LayoutProps {
  children: ReactNode
  onActivityClick?: () => void
  onCurrencyClick?: () => void
  inProgressCount?: number
  fiatCurrency?: string
  fiatSymbol?: string
}

const LINKS = {
  twitter: 'https://x.com/parsedefi',
  help: 'mailto:help@parsedefi.com',
  // External Tally forms (open in a new tab). Feedback + API-waitlist signup.
  feedback: 'https://tally.so/r/PdrEEQ',
  apiWaitlist: 'https://tally.so/r/ZjeVYa',
  terms: '/terms',
  privacy: '/privacy',
}

export function Layout({
  children,
  onActivityClick,
  onCurrencyClick,
  inProgressCount = 0,
  fiatCurrency = 'usd',
  fiatSymbol = '$',
}: LayoutProps) {
  const { theme, toggle } = useTheme()
  const c = useThemeColors()
  const UI = useUI()
  const viewportSize = useViewportSize()
  const dark = theme === 'dark'
  const isMobile = viewportSize === 'small' || viewportSize === 'mobile'

  // All four header controls share this exact height so they line up uniformly. A FIXED height (not
  // minHeight) is required: text buttons (currency, and "Activity" on desktop) inherit line-height 1.5
  // from Tailwind's preflight, which computes ~40px, while icon-only buttons are 36px — pinning height
  // removes that mismatch (most visible on mobile, where the currency is the only text button). 36 keeps
  // them the same size as the icon squares (the reference), rather than growing the squares to match.
  const CTRL_H = 36

  // Referral "coming soon" popup (#32). Static, self-contained — no wallet/data/flag deps.
  const [referralModalOpen, setReferralModalOpen] = useState(false)

  // viewport-fit=cover paints edge-to-edge, so SOMETHING must absorb the top safe-area inset (status
  // bar / notch) or the topmost element tucks under it. When the beta banner renders it is the topmost
  // element and owns that inset (see BetaBanner); with the banner off (GA) the header is topmost, so the
  // root absorbs it here instead. Guarding on the flag keeps the inset consumed exactly once (no double gap).
  const bannerOn = import.meta.env.VITE_BETA_BANNER === 'true'

  // Header alignment: the content card below (BridgePage root) uses a fixed `0 16px` horizontal
  // padding at every breakpoint, but UI.headerPad scales its HORIZONTAL padding per tier (16 → 20 →
  // 24px). Since the header box and the card box share the same maxWidth (UI.cardMax) and center, a
  // wider header inset shifts the header's contents (logo / controls) inward relative to the card —
  // read by users as "the header is shifted a few px to the right" on desktop/wide. Keep the tier's
  // VERTICAL rhythm but pin the header's horizontal padding to the card's 16px so the two edges line up.
  const headerPadY = UI.headerPad.split(' ')[0] ?? '14px'

  return (
    <div style={{
      minHeight: '100dvh',
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      background: c.bg, fontFamily: FONT_BODY,
      // Top inset only when the banner is off (else the banner already consumed it). No-op where env → 0.
      paddingTop: bannerOn ? undefined : 'env(safe-area-inset-top)',
      // Bottom breathing room so the API card + footer clear a bottom browser toolbar
      // (Samsung Internet / iOS Safari) and the home-indicator/gesture bar. env() resolves to a
      // real value now that index.html sets viewport-fit=cover; 0 elsewhere so the max() floor holds.
      paddingBottom: 'max(20px, env(safe-area-inset-bottom))',
    }}>
      {/* Header */}
      <header style={{
        width: '100%', maxWidth: UI.cardMax, padding: `${headerPadY} 16px`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        gap: viewportSize === 'small' ? 10 : viewportSize === 'mobile' ? 14 : 16,
      }}>
        <Link to="/" style={{
          display: 'flex', alignItems: 'center',
          gap: viewportSize === 'small' ? 6 : 9,
          flexShrink: 0, textDecoration: 'none',
        }}>
          {/* The brand group (logo + wordmark) is rigid — flexShrink:0 on the Link keeps it at full size so
              flexbox never compresses it; the fixed-size right controls (icon-only on mobile) are the other
              rigid group, and the two fit the header at every tier. Do NOT put minWidth:0 / overflow:hidden
              on the wordmark: that lets flexbox shrink it a few px and clips the trailing "Fi" (the "i"). */}
          <span style={{ display: 'flex', flexShrink: 0 }}>
            <LogoMark size={UI.headerLogo} color={dark ? '#FFFFFF' : '#000000'} />
          </span>
          <h1 style={{
            fontFamily: FONT_HEADING, fontWeight: 600, fontSize: UI.headerFont,
            letterSpacing: -0.3, whiteSpace: 'nowrap', margin: 0, padding: 0,
          }}>
            <span style={{ color: dark ? '#FFFFFF' : '#000000' }}>Parse</span>{' '}
            <span style={{ fontWeight: 500, letterSpacing: 0.5 }}>
              <span style={{ color: BRAND_CYAN }}>De</span>
              <span style={{ color: BRAND_GREEN }}>Fi</span>
            </span>
          </h1>
        </Link>
        <div style={{ display: 'flex', gap: viewportSize === 'small' ? 5 : 8, alignItems: 'center' }}>
          {/* Referral entry point (#32): the real /refer pages stay redirected while referrals are
              toggled off backend-side — this icon only opens a static "coming soon" popup so
              prospective referrers can reach us. No wallet/data/REFERRALS_ENABLED dependency. */}
          <button
            onClick={() => setReferralModalOpen(true)}
            aria-label="Refer & Earn"
            style={{
              background: c.card, border: `1px solid ${c.cb}`, borderRadius: 10,
              // Symmetric 9px so this icon-only button renders a uniform 36×36 square (icon + 18px
              // padding + 2px border, border-box) — matching the activity icon button. The prior 10px
              // horizontal padding made it (and the theme toggle) 38px wide, so the gift/theme icons read
              // as noticeably more spaced-out than the USD / activity icons beside them.
              padding: '9px 9px',
              cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: c.mu, transition: 'all 0.2s',
              height: CTRL_H, minWidth: CTRL_H, boxSizing: 'border-box',
            }}
          >
            <Gift size={16} />
          </button>

          {/* Currency selector. DESKTOP/wide: the original globe + 3-letter code + chevron. MOBILE
              (small/mobile tiers): truncated to just the currency SYMBOL + chevron, so the row fits on
              narrow phones without clipping the rightmost icon. Fixed height (CTRL_H) + lineHeight 1 so
              this text button matches the icon squares' height instead of the ~40px its 1.5 line-height
              would otherwise compute. Full name is in the picker; aria-label carries the code for SRs. */}
          <button
            onClick={onCurrencyClick}
            aria-label={`Display currency (${fiatCurrency.toUpperCase()})`}
            style={{
              background: c.card, border: `1px solid ${c.cb}`, borderRadius: 10,
              padding: viewportSize === 'small' ? '8px 9px' : '8px 11px',
              cursor: 'pointer', display: 'flex',
              alignItems: 'center', gap: isMobile ? 4 : 6, color: c.ts, fontFamily: FONT_BODY,
              fontSize: viewportSize === 'small' ? 13 : 15, fontWeight: 600, lineHeight: 1,
              transition: 'all 0.2s', height: CTRL_H, boxSizing: 'border-box',
            }}
          >
            {!isMobile && <Globe size={15} style={{ color: c.mu }} />}
            <span style={{ letterSpacing: isMobile ? 0.2 : 0.4, fontWeight: 600 }}>
              {isMobile ? fiatSymbol : fiatCurrency.toUpperCase()}
            </span>
            <ChevronDown size={viewportSize === 'small' ? 11 : 13} style={{ color: c.mu, marginLeft: isMobile ? 0 : -1 }} />
          </button>

          {/* Activity button */}
          <button
            onClick={onActivityClick}
            aria-label="Open activity"
            style={{
              background: c.card, border: `1px solid ${c.cb}`, borderRadius: 10,
              padding: isMobile ? '8px 8px' : '8px 10px 8px 9px',
              cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: isMobile ? 'center' : undefined,
              gap: 5, color: c.ts, fontFamily: FONT_BODY,
              fontSize: viewportSize === 'small' ? 12 : 14, fontWeight: 500, lineHeight: 1,
              transition: 'all 0.2s', position: 'relative',
              height: CTRL_H, boxSizing: 'border-box', ...(isMobile ? { minWidth: CTRL_H } : {}),
            }}
          >
            <History size={viewportSize === 'small' ? 14 : 15} />
            {/* Icon-only on mobile (both 'small' and 'mobile' tiers): the "Activity" text widened the
                right control group past a ~390px viewport, pressuring the left group so the logo clipped.
                The history icon is self-explanatory; the label returns at the desktop tier. */}
            {!isMobile && <span>Activity</span>}
            {inProgressCount > 0 && (
              <span style={{
                background: c.ac,
                color: dark ? c.bg : '#fff',
                fontSize: 10, fontWeight: 700, fontFamily: FONT_HEADING,
                borderRadius: 999, padding: viewportSize === 'small' ? '1px 5px' : '1px 6px',
                minWidth: 16, textAlign: 'center', lineHeight: 1.4,
              }}>
                {inProgressCount > 99 ? '99+' : inProgressCount}
              </span>
            )}
          </button>

          {/* Theme toggle */}
          <button
            onClick={toggle}
            aria-label={dark ? 'Switch to light theme' : 'Switch to dark theme'}
            aria-pressed={dark}
            style={{
              background: c.card, border: `1px solid ${c.cb}`, borderRadius: 10,
              // Uniform icon square (see the gift button) so the theme toggle lines up with the others.
              padding: '9px 9px',
              cursor: 'pointer', display: 'flex',
              alignItems: 'center', justifyContent: 'center',
              color: c.mu, transition: 'all 0.2s',
              height: CTRL_H, minWidth: CTRL_H, boxSizing: 'border-box',
            }}
          >
            {dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </div>
      </header>

      {/* Main content */}
      <main id="main-content" tabIndex={-1} style={{ width: '100%', flex: isMobile ? undefined : 0 }}>
        {children}
      </main>

      {/* API Coming Soon Banner */}
      <div style={{ width: '100%', maxWidth: UI.cardMax, padding: '0 16px', marginTop: 10 }}>
        <a
          href={LINKS.apiWaitlist}
          target="_blank"
          rel="noopener noreferrer"
          className="hover-tint"
          style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: viewportSize === 'small' ? '10px 12px' : '11px 14px',
            borderRadius: 12, background: c.card, border: `1px solid ${c.dv}`,
            color: c.mu, textDecoration: 'none', fontFamily: FONT_BODY,
            transition: 'all 0.2s', gap: 8,
            '--hover-border': c.ac + '55',
            '--hover-color': c.mu,
          } as React.CSSProperties}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: viewportSize === 'small' ? 8 : 10, minWidth: 0, flex: 1 }}>
            <div style={{
              width: viewportSize === 'small' ? 24 : 26,
              height: viewportSize === 'small' ? 24 : 26,
              borderRadius: '50%', background: c.aB,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Zap size={viewportSize === 'small' ? 12 : 13} color={c.ac} strokeWidth={2} />
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{
                fontSize: viewportSize === 'small' ? 12 : 13,
                fontWeight: 600, color: c.tx, lineHeight: 1.3,
              }}>API coming soon</div>
              <div style={{
                fontSize: viewportSize === 'small' ? 10 : 11,
                color: c.mu, marginTop: 1, lineHeight: 1.3,
              }}>
                {viewportSize === 'small' ? 'Request early access' : 'Building something? Request early access.'}
              </div>
            </div>
          </div>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 4,
            fontSize: viewportSize === 'small' ? 11 : 12,
            fontWeight: 500, color: c.ac, flexShrink: 0,
          }}>
            <span>{viewportSize === 'small' ? 'Join' : 'Join waitlist'}</span>
            <ArrowRight size={viewportSize === 'small' ? 11 : 12} />
          </div>
        </a>
      </div>

      {/* Desktop spacer */}
      {!isMobile && <div style={{ flex: 1 }} />}

      {/* Footer */}
      <footer style={{
        width: '100%', maxWidth: 480, padding: '0 20px',
        marginTop: isMobile ? 20 : 40,
      }}>
        <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginBottom: 14, flexWrap: 'wrap' }}>
          {[
            {
              icon: (
                <svg width="13" height="13" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              ),
              label: 'Follow us',
              href: LINKS.twitter,
            },
            // 'Guides' footer link removed — no guides / API-docs page exists yet.
            // 'Refer & Earn' footer link hidden until referrals are enabled per-chain; re-show
            // should gate on a server-exposed referrals-enabled flag (follow-up). /refer page still exists.
            { icon: <HelpCircle size={14} />, label: 'Help', href: LINKS.help },
            { icon: <MessageCircle size={14} />, label: 'Feedback', href: LINKS.feedback },
            { icon: <Zap size={14} />, label: 'API waitlist', href: LINKS.apiWaitlist },
          ].map(l => {
            const isPlaceholder = l.href === '#'
            const isInternal = l.href.startsWith('/')
            if (isInternal) {
              return (
                <Link
                  key={l.label}
                  to={l.href}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    color: c.fC, textDecoration: 'none', fontSize: 13,
                    fontWeight: 500, fontFamily: FONT_BODY, transition: 'color 0.15s',
                  }}
                >
                  {l.icon}{l.label}
                </Link>
              )
            }
            return (
              <a
                key={l.label}
                href={l.href}
                onClick={isPlaceholder ? (e) => e.preventDefault() : undefined}
                target={isPlaceholder ? undefined : '_blank'}
                rel={isPlaceholder ? undefined : 'noopener noreferrer'}
                style={{
                  display: 'flex', alignItems: 'center', gap: 5,
                  color: c.fC, textDecoration: 'none', fontSize: 13,
                  fontWeight: 500, fontFamily: FONT_BODY, transition: 'color 0.15s',
                }}
              >
                {l.icon}{l.label}
              </a>
            )
          })}
        </div>
        <div style={{ textAlign: 'center', marginBottom: 12 }}>
          <a href="mailto:info@parsedefi.com" style={{ color: c.ac, textDecoration: 'none', fontSize: 13, fontFamily: FONT_BODY }}>
            info@parsedefi.com
          </a>
        </div>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '12px 0', borderTop: `1px solid ${c.dv}`, marginBottom: 16,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: c.fC }}>
            <Shield size={12} />Secured transfers
          </div>
          <div style={{ display: 'flex', gap: 12 }}>
            {[
              { label: 'Terms', href: LINKS.terms },
              { label: 'Privacy', href: LINKS.privacy },
            ].map(l => {
              // Internal routes must go through react-router <Link> (SPA nav), not a plain <a> —
              // an <a href="/terms"> would trigger a full page reload.
              const isInternal = l.href.startsWith('/')
              const style = { color: c.fC, textDecoration: 'none', fontSize: 11 }
              if (isInternal) {
                return (
                  <Link key={l.label} to={l.href} style={style}>
                    {l.label}
                  </Link>
                )
              }
              return (
                <a
                  key={l.label}
                  href={l.href}
                  onClick={l.href === '#' ? (e) => e.preventDefault() : undefined}
                  style={style}
                >
                  {l.label}
                </a>
              )
            })}
          </div>
        </div>
      </footer>

      <ReferralComingSoonModal
        show={referralModalOpen}
        onClose={() => setReferralModalOpen(false)}
      />
    </div>
  )
}
